import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface PublicRouteProps {
  children: React.ReactNode;
}

/**
 * PublicRoute component that redirects authenticated users to the dashboard
 * This is used for routes like signin and landing page that should only be accessible to non-authenticated users
 */
const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, checkAndRedirectIfAuthenticated } = useAuth();
  const [checking, setChecking] = useState(true);

  // Check auth once on mount
  useEffect(() => {
    // Skip auth check if we already know the user is not authenticated
    if (!isAuthenticated && !loading) {
      setChecking(false);
      return;
    }
    
    // If auth state is pending or user might be authenticated, verify once
    const verifyAuth = async () => {
      try {
        // This will automatically redirect to dashboard if authenticated
        await checkAndRedirectIfAuthenticated(true);
        setChecking(false);
      } catch (error) {
        console.error('Error verifying authentication:', error);
        setChecking(false);
      }
    };

    verifyAuth();
    // We only want to check once on mount - dependency array should be empty
  }, []);

  // Show loading spinner while checking authentication status
  if (loading || checking) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 size={36} className="animate-spin text-blue-600" />
      </div>
    );
  }

  // Redirect to dashboard if authenticated (in case checkAndRedirectIfAuthenticated didn't)
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Render children if not authenticated
  return <>{children}</>;
};

export default PublicRoute;